import React, { useState } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import api from '../../utils/api';

interface WebAuthnLoginProps {
  onSuccess?: (credentialResponse: any) => void;
  onError?: (error: string) => void;
  disabled?: boolean;
  mfaMode?: boolean; // New prop to indicate MFA mode vs standalone login
  mfaToken?: string; // AUTH-12: post-password MFA challenge token (MFA mode)
}

function WebAuthnLogin({ onSuccess, onError, disabled = false, mfaMode = false, mfaToken }: WebAuthnLoginProps) {
  const [loading, setLoading] = useState(false);
  const webAuthnSupported = 
    window.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential === 'function';

  const handleWebAuthnLogin = async () => {
    if (!webAuthnSupported) {
      onError?.('WebAuthn is not supported in this browser');
      return;
    }

    setLoading(true);
    
    try {
      // Start authentication
      const requestBody = mfaMode && mfaToken ? { mfaToken } : {};
      const optionsResponse = await api.post('/api/webauthn/authenticate/begin', requestBody);
      const options = optionsResponse.data.options;

      // Use browser WebAuthn API
      const credential = await startAuthentication(options);

      if (mfaMode) {
        // In MFA mode, just return the credential response for the parent to handle
        onSuccess?.(credential);
      } else {
        // Standalone WebAuthn login mode
        const verificationResponse = await api.post('/api/webauthn/authenticate/finish', {
          response: credential
        });

        if (verificationResponse.data.success) {
          // Get auth token (you might need to modify this based on your auth flow)
          const loginResponse = await api.post('/api/auth/webauthn-login', {
            verified: true
          });
          
          if (loginResponse.data.token) {
            onSuccess?.(loginResponse.data.token);
          } else {
            onError?.('Authentication successful but no token received');
          }
        } else {
          onError?.('Failed to verify authentication');
        }
      }
    } catch (error: any) {
      console.error('WebAuthn authentication error:', error);
      
      let errorMessage = 'Authentication failed';
      
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Authentication was cancelled or not allowed';
      } else if (error.name === 'InvalidStateError') {
        errorMessage = 'Authentication failed - invalid state';
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'This authenticator is not supported';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!webAuthnSupported) {
    return null; // Don't show WebAuthn option if not supported
  }

  return (
    <div>
      {!mfaMode && (
        <div className="flex items-center my-4">
          <div className="flex-grow border-t border-gray-300"></div>
          <span className="flex-shrink mx-4 text-gray-500 text-sm">OR</span>
          <div className="flex-grow border-t border-gray-300"></div>
        </div>
      )}

      <button
        onClick={handleWebAuthnLogin}
        disabled={loading || disabled}
        className={`w-full flex items-center justify-center px-4 py-3 border border-blue-500 rounded-md text-blue-500 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
          mfaMode ? 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600' : ''
        }`}
      >
        {loading ? (
          <>
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Authenticating...
          </>
        ) : (
          <>
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
            </svg>
            {mfaMode ? 'Authenticate with Biometrics' : 'Sign in with Biometrics'}
          </>
        )}
      </button>

      <p className="mt-2 text-center text-xs text-gray-400">
        Use your fingerprint, face recognition, or security key
      </p>
    </div>
  );
}

export default WebAuthnLogin;
